import { useCallback, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';

/**
 * usePayFastInitiate — kicks off a PayFast checkout for an unpaid invoice.
 *
 * Phase 9 Slice 9.1 (BRD §7.1, architecture §4.1). The hook:
 *   1. Generates a fresh idempotency-key per invocation (allowing the
 *      customer to retry after abandoning PayFast — same key inside 24h
 *      returns the same redirect; new key starts a fresh attempt).
 *   2. POSTs to the `payfast-initiate` Edge Function with the customer's
 *      JWT (verify_jwt is on for that EF).
 *   3. Receives `{ action_url, fields }` back; mounts a hidden HTML form,
 *      auto-submits to PayFast. The browser then navigates to PayFast's
 *      hosted checkout — `isLoading` stays true through that navigation.
 *
 * The truth-bearing write happens server-side via the payfast-webhook ITN,
 * not in this hook. After the customer pays, PayFast redirects them to
 * `/app/billing/invoices/:id?paid=1`. The InvoiceDetailPage handles the
 * cosmetic side: refetches the invoice and shows a toast. Success/failure
 * UI state derives from the refetched `invoice.lifecycle_state`, NEVER
 * from the URL param (review fix R1).
 */

export interface PayFastInitiateError {
  code: string;
  message: string;
}

export interface UsePayFastInitiateResult {
  initiate: () => Promise<void>;
  isLoading: boolean;
  error: PayFastInitiateError | null;
}

interface PayFastInitiateResponse {
  ok: true;
  action_url: string;
  fields: Record<string, string>;
  idempotency_key: string;
}

export function usePayFastInitiate(invoiceId: string): UsePayFastInitiateResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<PayFastInitiateError | null>(null);

  const initiate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const idempotencyKey = crypto.randomUUID();

      const { data, error: efError } = await supabase.functions.invoke<PayFastInitiateResponse>(
        'payfast-initiate',
        {
          body: { invoice_id: invoiceId },
          headers: { 'idempotency-key': idempotencyKey },
        }
      );

      if (efError) {
        // FunctionsHttpError (4xx/5xx) — try to parse the EF's structured
        // error body so we can show a friendly message.
        const code = await extractErrorCode(efError);
        setError({ code, message: friendlyErrorMessage(code) });
        setIsLoading(false);
        return;
      }

      if (!data?.ok) {
        setError({ code: 'unknown', message: friendlyErrorMessage('unknown') });
        setIsLoading(false);
        return;
      }

      // Build a hidden form, append all fields, submit. Vanilla DOM —
      // intentionally bypasses React's reconciliation so the form lives
      // exactly long enough to dispatch a navigation, then is gone.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.action_url;
      form.style.display = 'none';
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      // Browser navigates here. isLoading stays true through the navigation
      // — no need to reset; the page is unmounting anyway.
    } catch (err) {
      setError({ code: 'unknown', message: (err as Error).message });
      setIsLoading(false);
    }
  }, [invoiceId]);

  return { initiate, isLoading, error };
}

async function extractErrorCode(err: unknown): Promise<string> {
  // Supabase v2 FunctionsHttpError attaches the underlying Response on
  // `context.response`. Try to read the JSON `code` from it; fall back to
  // 'unknown' if anything goes wrong.
  try {
    const ctx = (err as { context?: { response?: Response } }).context;
    if (ctx?.response) {
      const body = await ctx.response.clone().json();
      if (typeof body?.code === 'string') return body.code;
    }
  } catch {
    /* ignore */
  }
  return 'unknown';
}

function friendlyErrorMessage(code: string): string {
  switch (code) {
    case 'invoice_already_paid':
      return 'This invoice is already paid.';
    case 'invoice_not_payable':
      return 'This invoice can no longer be paid online.';
    case 'invoice_zero_amount':
      return 'There is no amount due on this invoice.';
    case 'invoice_not_found':
      return "We couldn't find that invoice on your account.";
    case 'forbidden_not_owner':
      return "You don't have permission to pay this invoice.";
    case 'unauthenticated':
      return 'Please sign in again to continue.';
    default:
      return 'Could not start checkout. Please try again or pay by EFT.';
  }
}
